import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const CourseLibraryManager = () => {
  const [activeType, setActiveType] = useState('category');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchItems();
  }, [activeType]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase?.from('global_course_catalog')?.select('*')?.eq('type', activeType)?.order('display_order', { ascending: true })?.order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching catalog items:', err);
      setError(err?.message || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formName?.trim()) {
      setSubmitError('Name is required');
      return;
    }
    try {
      setSubmitting(true);
      setSubmitError(null);
      const maxOrder = items?.length > 0 ? Math.max(...items?.map(i => i?.display_order || 0)) + 1 : 1;
      const { error: insertError } = await supabase?.from('global_course_catalog')?.insert({ name: formName?.trim(), type: activeType, display_order: maxOrder });

      if (insertError) throw insertError;
      setFormName('');
      setShowAddForm(false);
      await fetchItems();
    } catch (err) {
      setSubmitError(err?.message?.includes('unique') ? 'This name already exists' : (err?.message || 'Failed to add item'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!formName?.trim()) {
      setSubmitError('Name is required');
      return;
    }
    try {
      setSubmitting(true);
      setSubmitError(null);
      const { error: updateError } = await supabase?.from('global_course_catalog')?.update({ name: formName?.trim(), updated_at: new Date()?.toISOString() })?.eq('id', editingItem?.id);

      if (updateError) throw updateError;
      setEditingItem(null);
      setFormName('');
      await fetchItems();
    } catch (err) {
      setSubmitError(err?.message?.includes('unique') ? 'This name already exists' : (err?.message || 'Failed to update item'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const { error: updateError } = await supabase?.from('global_course_catalog')?.update({ is_active: !item?.is_active, updated_at: new Date()?.toISOString() })?.eq('id', item?.id);
      if (updateError) throw updateError;
      await fetchItems();
    } catch (err) {
      alert(err?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this item? Institutes using this value will retain their existing data.')) return;
    try {
      setDeletingId(id);
      const { error: deleteError } = await supabase?.from('global_course_catalog')?.delete()?.eq('id', id);
      if (deleteError) throw deleteError;
      await fetchItems();
    } catch (err) {
      alert(err?.message || 'Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormName(item?.name || '');
    setShowAddForm(false);
    setSubmitError(null);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingItem(null);
    setFormName('');
    setSubmitError(null);
  };

  const typeLabel = activeType === 'category' ? 'Category' : 'Level';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Course Library Manager</h2>
          <p className="text-muted-foreground mt-1">
            Manage course categories and levels that appear in the Institute Dashboard and bulk upload.
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          onClick={() => { setShowAddForm(true); setEditingItem(null); setFormName(''); setSubmitError(null); }}
        >
          Add {typeLabel}
        </Button>
      </div>
      {/* Type Tabs */}
      <div className="flex gap-2 border-b border-border">
        {['category', 'level']?.map((type) => (
          <button
            key={type}
            onClick={() => { setActiveType(type); setShowAddForm(false); setEditingItem(null); setFormName(''); }}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
              activeType === type
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {type === 'category' ? 'Course Categories' : 'Course Levels'}
            <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
              activeType === type ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
            }`}>
              {activeType === type ? items?.length : ''}
            </span>
          </button>
        ))}
      </div>
      {/* Add / Edit Form */}
      {(showAddForm || editingItem) && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {editingItem ? `Edit ${typeLabel}` : `Add New ${typeLabel}`}
          </h3>
          <div className="flex gap-3 items-start">
            <div className="flex-1">
              <Input
                value={formName}
                onChange={(e) => setFormName(e?.target?.value)}
                placeholder={`Enter ${typeLabel?.toLowerCase()} name`}
                onKeyDown={(e) => e?.key === 'Enter' && (editingItem ? handleEdit() : handleAdd())}
              />
              {submitError && (
                <p className="text-error text-xs mt-1">{submitError}</p>
              )}
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={editingItem ? handleEdit : handleAdd}
              disabled={submitting}
            >
              {submitting ? (
                <Icon name="Loader2" size={14} className="animate-spin" />
              ) : editingItem ? 'Save' : 'Add'}
            </Button>
            <Button variant="outline" size="sm" onClick={cancelForm} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={36} className="animate-spin" color="var(--color-primary)" />
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error text-error rounded-lg p-4">
          <p className="font-medium">Error loading {typeLabel?.toLowerCase()}s</p>
          <p className="text-sm mt-1">{error}</p>
          <Button onClick={fetchItems} variant="outline" size="sm" className="mt-3">Retry</Button>
        </div>
      ) : items?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center">
          <Icon name="BookOpen" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground">No {typeLabel?.toLowerCase()}s added yet.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => { setShowAddForm(true); setEditingItem(null); setFormName(''); }}
          >
            Add First {typeLabel}
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-border flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {items?.length} {typeLabel}{items?.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-muted-foreground">
              Changes reflect immediately in Institute Dashboard
            </span>
          </div>
          <ul className="divide-y divide-border">
            {items?.map((item) => (
              <li key={item?.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item?.is_active ? 'bg-success' : 'bg-gray-300'}`} />
                  <span className={`text-sm font-medium ${item?.is_active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                    {item?.name}
                  </span>
                  {!item?.is_active && (
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-muted-foreground rounded">Inactive</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(item)}
                    className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                      item?.is_active
                        ? 'bg-success/10 text-success hover:bg-success/20' :'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                    }`}
                    title={item?.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {item?.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => startEdit(item)}
                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                    title="Edit"
                  >
                    <Icon name="Pencil" size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(item?.id)}
                    disabled={deletingId === item?.id}
                    className="p-1.5 text-muted-foreground hover:text-error hover:bg-error/10 rounded-md transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    {deletingId === item?.id ? (
                      <Icon name="Loader2" size={14} className="animate-spin" />
                    ) : (
                      <Icon name="Trash2" size={14} />
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={16} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">How this works</p>
            <ul className="space-y-1 text-blue-600">
              <li>• Active categories and levels appear as dropdown options in the Institute Dashboard when adding courses.</li>
              <li>• They are also used to validate bulk CSV course uploads.</li>
              <li>• Deactivating an item hides it from new selections but does not affect existing course data.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseLibraryManager;
